import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Bell, Users, FileText, ChevronRight } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const PrivacySecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    profileVisibility: 'everyone',
    showAge: true,
    showDistance: true,
    allowMessages: 'matches',
    dataSharing: false,
    analytics: true,
    twoFactorAuth: false
  });

  const privacySettings = [
    {
      icon: Eye,
      title: 'Profile Visibility',
      description: 'Who can see your profile',
      value: settings.profileVisibility,
      options: ['everyone', 'verified', 'premium'],
      key: 'profileVisibility'
    },
    {
      icon: Users,
      title: 'Show Age',
      description: 'Display your age on profile',
      value: settings.showAge,
      key: 'showAge',
      type: 'toggle'
    },
    {
      icon: Users,
      title: 'Show Distance',
      description: 'Display distance to other users',
      value: settings.showDistance,
      key: 'showDistance',
      type: 'toggle'
    },
    {
      icon: Bell,
      title: 'Allow Messages From',
      description: 'Who can send you messages',
      value: settings.allowMessages,
      options: ['everyone', 'matches', 'premium'],
      key: 'allowMessages'
    }
  ];

  const securitySettings = [
    {
      icon: Lock,
      title: 'Two-Factor Authentication',
      description: 'Add extra security to your account',
      value: settings.twoFactorAuth,
      key: 'twoFactorAuth',
      type: 'toggle'
    },
    {
      icon: Shield,
      title: 'Data Sharing',
      description: 'Share data with partners for better matches',
      value: settings.dataSharing,
      key: 'dataSharing',
      type: 'toggle'
    },
    {
      icon: FileText,
      title: 'Analytics',
      description: 'Help improve the app with usage data',
      value: settings.analytics,
      key: 'analytics',
      type: 'toggle'
    }
  ];

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const renderSetting = (setting: any) => {
    if (setting.type === 'toggle') {
      return (
        <Card key={setting.key} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                <setting.icon size={20} className="text-purple-600 dark:text-purple-300" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">{setting.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{setting.description}</p>
              </div>
            </div>
            <button
              onClick={() => handleSettingChange(setting.key, !setting.value)}
              className={`w-12 h-6 rounded-full transition-colors ${
                setting.value ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full transition-transform ${
                  setting.value ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </Card>
      );
    }

    return (
      <Card key={setting.key} className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
              <setting.icon size={20} className="text-purple-600 dark:text-purple-300" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">{setting.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">{setting.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">{setting.value}</span>
            <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Privacy & Security</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Privacy Settings */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Privacy Settings</h2>
          <div className="space-y-3">
            {privacySettings.map(renderSetting)}
          </div>
        </div>

        {/* Security Settings */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Security Settings</h2>
          <div className="space-y-3">
            {securitySettings.map(renderSetting)}
          </div>
        </div>

        {/* Privacy Policy */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Legal</h2>
          <div className="space-y-3">
            <Card 
              onClick={() => navigate('/privacy-policy')}
              className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <FileText size={20} className="text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">Privacy Policy</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Read our privacy policy</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
              </div>
            </Card>

            <Card 
              onClick={() => navigate('/terms-of-service')}
              className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <Shield size={20} className="text-green-600 dark:text-green-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">Terms of Service</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Read our terms of service</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
              </div>
            </Card>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={() => {
            alert('Settings saved successfully!');
            navigate('/settings');
          }}
          variant="gradient"
          size="lg"
          className="w-full"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default PrivacySecurityPage;